import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Palette } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { type SelectPostLayoutFn } from "@/components/layouts/LayoutPickerSection";
import BrandKitSubtitlePreview from "@/components/BrandKitSubtitlePreview";
import { StyleSidePanel } from "@/components/editor/StyleSidePanel";
import ProjectPreview from "@/components/ProjectPreview";
import { POST_LAYOUTS } from "@/lib/layouts/registry";
import { applyLayoutBinds, type LayoutFieldBinds } from "@/lib/layouts/layoutFieldBinds";
import { applyMaterializedFillers } from "@/lib/layouts/fillerPlaylist";
import { resolveEditorPreviewPostLayout } from "@/lib/layouts/resolveEditorPreviewPostLayout";
import { resolveFields } from "@/lib/layouts/resolveFields";
import type { ResolvedPersistedClipPreview } from "@/lib/preview/resolvePersistedClipPreview";
import type { ComponentProps } from "react";
import CompositionFitOverlay from "@/components/layers/CompositionFitOverlay";
import { PreviewChromeEditors } from "@/components/layouts/PreviewChromeEditors";
import { useWatermarkVisibility } from "@/hooks/use-watermark-visibility";
import type { WatermarkPosition } from "@/lib/watermark/watermarkPosition";
import {
  defaultLogoData,
  defaultSubtitleData,
} from "./clipsStyleDefaults";

type StyleSidePanelProps = ComponentProps<typeof StyleSidePanel>;

interface ClipsStyleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtitlesEnabled: boolean;
  onSubtitlesEnabledChange: (enabled: boolean) => void;
  userBrandKits: any[];
  publicBrandKits: any[];
  selectedBrandKitId: string | null;
  onSelectBrandKit: (kit: any) => void;
  subtitleData: typeof defaultSubtitleData;
  updateSubtitle: (field: string, value: any) => void;
  selectedSubtitleFontId: string;
  onSelectSubtitleFont: (fontId: string, font: any) => void;
  getFontFamilyName: (fontId: string) => string | null;
  logoData: typeof defaultLogoData;
  updateLogo: (field: string, value: any) => void;
  onLogoUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  isUploadingLogo: boolean;
  enforcedSubtitlePos: { locked: boolean; alignItems: string; verticalMargin: number } | null;
  fallbackPreviewKit: any | null;
  videoUrl: string;
  previewProcessing: ResolvedPersistedClipPreview | null;
  cropCenterKeyframes: any[];
  previewSpeakerLayout: any;
  subtitleConfig: any;
  logoConfig: any;
  previewTranscriptionSegments: any;
  postLayout: any;
  showWatermark: boolean;
  projectDocumentId?: string;
  libraryDocumentId?: string;
  watermarkPosition?: WatermarkPosition | null;
  onWatermarkPositionChange?: (next: WatermarkPosition) => void;
  onWatermarkPositionReset?: () => void;
  effectivePostLayout: any;
  uniqueSpeakerCount: number;
  selectedSpeakerLayout: number;
  onSelectSpeakerLayout: (num: number) => void;
  onSelectPostLayout: SelectPostLayoutFn;
  onUpdatePostLayoutField: (key: string, value: string | number | string[]) => void;
  postLayoutDisabledKeys?: string[];
  fieldBinds?: LayoutFieldBinds;
  onFieldBindChange?: StyleSidePanelProps["onFieldBindChange"];
  bindContext?: StyleSidePanelProps["bindContext"];
  fillerPool?: string[];
  previewClipDurationSec?: number;
  onPostLayoutPatch?: (patch: Record<string, unknown>) => void;
}

export default function ClipsStyleSheet(props: ClipsStyleSheetProps) {
  const { t } = useTranslation();
  const {
    open,
    onOpenChange,
    subtitlesEnabled,
    onSubtitlesEnabledChange,
    selectedBrandKitId,
    subtitleData,
    updateSubtitle,
    selectedSubtitleFontId,
    onSelectSubtitleFont,
    logoData,
    updateLogo,
    onLogoUpload,
    isUploadingLogo,
    fallbackPreviewKit,
    videoUrl,
    previewProcessing,
    cropCenterKeyframes,
    previewSpeakerLayout,
    subtitleConfig,
    logoConfig,
    previewTranscriptionSegments,
    postLayout,
    showWatermark,
    projectDocumentId,
    libraryDocumentId,
    watermarkPosition,
    onWatermarkPositionChange,
    onWatermarkPositionReset,
    effectivePostLayout,
    uniqueSpeakerCount,
    selectedSpeakerLayout,
    onSelectSpeakerLayout,
    onSelectPostLayout,
    onUpdatePostLayoutField,
    postLayoutDisabledKeys,
    fieldBinds,
    onFieldBindChange,
    bindContext,
    fillerPool,
    previewClipDurationSec,
    onPostLayoutPatch,
  } = props;
  const { showDefaultWatermark } = useWatermarkVisibility({
    showWatermark,
    projectDocumentId,
    libraryDocumentId,
  });

  const previewPostLayout = (() => {
    const resolved = resolveEditorPreviewPostLayout({
      postLayout,
      effectiveAspectRatio: "portrait",
    });
    if (!resolved) return resolved;
    const withFillers = applyMaterializedFillers(resolved, {
      pool: fillerPool,
      targetDurationSec: previewClipDurationSec,
    });
    if (!fieldBinds || !bindContext) return withFillers;
    return applyLayoutBinds(withFillers, fieldBinds, {
      clipTitle: bindContext.clipTitle,
      channelName: bindContext.channelName,
      channelAvatarUrl: bindContext.channelAvatarUrl,
      userName: bindContext.userName,
      userAvatarUrl: bindContext.userAvatarUrl,
    });
  })();
  const previewLayoutKey = postLayout
    ? `post-${postLayout.kind}`
    : `speaker-${selectedSpeakerLayout}`;
  const previewCropCenterKeyframes = (
    previewProcessing
    && previewProcessing.cropCenterKeyframes.length > 0
  )
    ? previewProcessing.cropCenterKeyframes
    : cropCenterKeyframes;

  const postLayoutFields = postLayout
    ? resolveFields(POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS], postLayout)
    : [];

  const postLayoutTitle = postLayout
    ? t(POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS]?.labelKey ?? "")
    : "";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md lg:max-w-2xl flex flex-col gap-0 p-0"
      >
        <SheetHeader className="px-4 py-3 border-b border-border/50 space-y-0">
          <div className="flex items-start justify-between gap-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="p-1 rounded-md bg-primary/15">
                <Palette className="w-3.5 h-3.5 text-primary" />
              </div>
              <SheetTitle>{t("projectClips.styleConfig.stylize")}</SheetTitle>
            </div>
            <div className="pr-8 flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">{t("projectClips.styleConfig.subtitlesToggle")}</Label>
              <Switch
                checked={subtitlesEnabled}
                onCheckedChange={onSubtitlesEnabledChange}
                className="data-[state=checked]:bg-primary flex-shrink-0"
              />
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {subtitlesEnabled && (
            <div className="flex flex-col lg:flex-row">
              {selectedBrandKitId !== null && (
                <div className="z-10 bg-background border-b border-border/50 p-3 flex flex-col items-center justify-center lg:sticky lg:top-0 lg:self-start lg:border-b-0 lg:border-r lg:border-border/40 lg:bg-black/20 lg:w-[280px] lg:shrink-0 lg:items-center">
                  <div className="h-[180px] aspect-[9/16] lg:h-auto lg:w-full lg:aspect-[9/16] lg:max-w-[260px] rounded-lg overflow-hidden border border-white/10 bg-black relative">
                    {videoUrl && previewProcessing?.startTime !== undefined && previewProcessing.endTime !== undefined ? (
                      <>
                        <div className="w-full h-full pointer-events-none [&_video]:pointer-events-none">
                          <ProjectPreview
                            key={previewLayoutKey}
                            videoUrl={videoUrl}
                            startTime={previewProcessing.startTime}
                            endTime={previewProcessing.endTime}
                            intervals={previewProcessing.intervals}
                            width={1080}
                            height={1920}
                            autoPlay
                            loop
                            volume={0}
                            showVolumeControls={false}
                            cropCenterKeyframes={
                              previewPostLayout || !previewSpeakerLayout
                                ? previewCropCenterKeyframes
                                : undefined
                            }
                            logoConfig={logoConfig}
                            subtitlesEnabled={subtitlesEnabled && !!previewTranscriptionSegments}
                            transcriptionSegments={previewTranscriptionSegments}
                            subtitleConfig={subtitleConfig}
                            speakerLayout={previewPostLayout ? undefined : previewSpeakerLayout}
                            postLayout={previewPostLayout}
                            videoEffects={previewProcessing.videoEffects}
                            transitions={previewProcessing.transitions}
                            keyframeTransitions={previewProcessing.keyframeTransitions}
                            layers={previewProcessing.layers}
                            showWatermark={showWatermark}
                            watermarkPosition={watermarkPosition}
                            projectDocumentId={projectDocumentId}
                            libraryDocumentId={libraryDocumentId}
                            style={{ width: "100%", height: "100%" }}
                          />
                        </div>
                        <CompositionFitOverlay compAspect={1080 / 1920}>
                          <PreviewChromeEditors
                            subtitlesEnabled={subtitlesEnabled}
                            subtitlePosition={subtitleData.position}
                            subtitleMarginVertical={subtitleData.marginVertical}
                            onSubtitleChange={(next) => {
                              updateSubtitle("position", next.position);
                              updateSubtitle("marginVertical", next.marginVertical);
                            }}
                            postLayout={previewPostLayout ?? postLayout}
                            layoutLocked={!!effectivePostLayout}
                            watermark={showDefaultWatermark && onWatermarkPositionChange && onWatermarkPositionReset ? {
                              position: watermarkPosition ?? null,
                              frameAspect: 1080 / 1920,
                              onChange: onWatermarkPositionChange,
                              onReset: onWatermarkPositionReset,
                            } : null}
                            onPostLayoutPatch={onPostLayoutPatch ?? (() => undefined)}
                          />
                        </CompositionFitOverlay>
                      </>
                    ) : (
                      <BrandKitSubtitlePreview brandKit={fallbackPreviewKit} />
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center mt-1.5">{t("configureSubtitles.editor.livePreview")}</p>
                </div>
              )}

              <div className="flex-1 min-w-0 px-4 py-2.5">
                <StyleSidePanel
                  uniqueSpeakerCount={uniqueSpeakerCount}
                  selectedSpeakerLayout={selectedSpeakerLayout}
                  onSelectSpeakerLayout={onSelectSpeakerLayout}
                  postLayout={postLayout}
                  onSelectPostLayout={onSelectPostLayout}
                  speakerOptions={[1]}
                  layoutFields={postLayoutFields}
                  layoutConfig={postLayout}
                  onLayoutFieldChange={onUpdatePostLayoutField}
                  videoFrameSource={
                    videoUrl && previewProcessing?.startTime !== undefined
                      ? {
                          videoUrl,
                          time: previewProcessing.startTime,
                          captureLabel: "playhead",
                        }
                      : undefined
                  }
                  disabledKeys={postLayoutDisabledKeys}
                  fieldBinds={fieldBinds}
                  onFieldBindChange={onFieldBindChange}
                  bindContext={bindContext}
                  enforcedNotice={effectivePostLayout ? (
                    <div
                      data-testid="enforced-layout-notice"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground"
                    >
                      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {t("projectClips.enforcedLayout.notice")}
                        {postLayout && POST_LAYOUTS[postLayout.kind as keyof typeof POST_LAYOUTS] && (
                          <> — <span className="font-medium text-foreground">{postLayoutTitle}</span></>
                        )}
                      </span>
                    </div>
                  ) : undefined}
                  subtitleData={subtitleData}
                  onSubtitleChange={updateSubtitle}
                  hidePathControls={postLayout?.kind === "circulo"}
                  logoData={logoData}
                  onLogoChange={updateLogo}
                  selectedSubtitleFontId={selectedSubtitleFontId}
                  onFontSelect={onSelectSubtitleFont}
                  onLogoUpload={onLogoUpload}
                  isUploadingLogo={isUploadingLogo}
                />
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
